import React from 'react';
import { clsx } from 'clsx';

export interface ChipProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string | number;
  color?: 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  size?: 'small' | 'medium';
  variant?: 'filled' | 'outlined';
  onDelete?: () => void;
  icon?: React.ReactNode;
}

const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
  (
    {
      label,
      color = 'default',
      size = 'medium',
      variant = 'filled',
      onDelete,
      icon,
      className,
      ...props
    },
    ref
  ) => {
    const classes = clsx(
      'inline-flex items-center gap-1 rounded-full font-medium transition-all',
      {
        // Size variants
        'px-2 py-0.5 text-xs': size === 'small',
        'px-3 py-1 text-sm': size === 'medium',

        // Filled variants
        'bg-gray-100 text-gray-800': variant === 'filled' && color === 'default',
        'bg-primary text-white': variant === 'filled' && color === 'primary',
        'bg-secondary text-white': variant === 'filled' && color === 'secondary',
        'bg-success text-white': variant === 'filled' && color === 'success',
        'bg-error text-white': variant === 'filled' && color === 'error',
        'bg-warning text-white': variant === 'filled' && color === 'warning',
        'bg-info text-white': variant === 'filled' && color === 'info',

        // Outlined variants
        'border border-gray-300 text-gray-700 bg-white': variant === 'outlined' && color === 'default',
        'border-2 border-primary text-primary bg-white': variant === 'outlined' && color === 'primary',
        'border-2 border-secondary text-secondary bg-white': variant === 'outlined' && color === 'secondary',
        'border-2 border-success text-success bg-white': variant === 'outlined' && color === 'success',
        'border-2 border-error text-error bg-white': variant === 'outlined' && color === 'error',
        'border-2 border-warning text-warning bg-white': variant === 'outlined' && color === 'warning',
        'border-2 border-info text-info bg-white': variant === 'outlined' && color === 'info',
      },
      className
    );

    return (
      <div ref={ref} className={classes} {...props}>
        {icon && <span className="inline-flex">{icon}</span>}
        <span>{label}</span>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center justify-center hover:bg-black hover:bg-opacity-10 rounded-full p-0.5 transition-colors"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    );
  }
);

Chip.displayName = 'Chip';

export default Chip;

